import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Table,
  InputNumber,
  Row,
  Col,
  Typography,
  Alert,
  message,
  Spin
} from 'antd';
import dayjs from 'dayjs';
import { unifiedShipmentsAPI, supplierAPI } from '../services/api.ts';
import {
  UnifiedShipment,
  ShipmentPackingListItem,
  GenerateLoadDocumentPayload,
  LoadDocumentType,
  RectificationReason
} from '../types.ts';

const { Text } = Typography;
const { Option } = Select;

interface ShipmentLoadModalProps {
  visible: boolean;
  shipment: UnifiedShipment | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const DOCUMENT_TYPE_OPTIONS: { value: LoadDocumentType; label: string }[] = [
  { value: 'DDT', label: 'DDT' },
  { value: 'PACKING_LIST', label: 'Packing List' },
  { value: 'ALTRO', label: 'Altro' },
];

/**
 * Documento di carico di una spedizione NAZIONALE/UE arrivata (senza dogana):
 * crea in BOZZA un documento DDT / Packing List / Altro con i pezzi ricevuti e
 * l'UDC di ogni riga. La merce NON si muove qui: entra in magazzino con l'Elabora
 * del documento in Documenti di Carico (come IM7/IM4 per le EXTRA_UE).
 * Ricevuto PARZIALE: il resto resta in transito e potrà entrare con un altro
 * documento; ricevuto SUPERIORE: la partita viene rettificata con il motivo.
 */
const ShipmentLoadModal: React.FC<ShipmentLoadModalProps> = ({
  visible,
  shipment,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [items, setItems] = useState<ShipmentPackingListItem[]>([]);
  const [received, setReceived] = useState<Record<number, number>>({});
  const [udcPallet, setUdcPallet] = useState<Record<number, string>>({});
  const [udcCollo, setUdcCollo] = useState<Record<number, string>>({});
  const [reasons, setReasons] = useState<RectificationReason[]>([]);

  useEffect(() => {
    if (visible && shipment) {
      loadData(shipment.id);
      form.resetFields();
      form.setFieldsValue({ document_type: 'DDT', document_date: dayjs(), rectification_reason_code: 'SUPPLIER_OVERSHIP' });
      supplierAPI.getRectificationReasons().then(setReasons).catch(() => setReasons([]));
    }
  }, [visible, shipment]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async (shipmentId: number) => {
    setLoadingData(true);
    try {
      const packing = await unifiedShipmentsAPI.getPackingList(shipmentId);
      const loadedItems: ShipmentPackingListItem[] = packing?.items || [];
      setItems(loadedItems);
      // Default: ricevuto = pezzi della riga non ancora messi in un documento di carico
      const initialReceived: Record<number, number> = {};
      const initialPallet: Record<number, string> = {};
      const initialCollo: Record<number, string> = {};
      loadedItems.forEach((it) => {
        initialReceived[it.id] = toLoad(it);
        initialPallet[it.id] = '';
        initialCollo[it.id] = '';
      });
      setReceived(initialReceived);
      setUdcPallet(initialPallet);
      setUdcCollo(initialCollo);
    } catch (error) {
      console.error('Errore nel caricamento dei dati spedizione:', error);
      message.error('Errore nel caricamento dei prodotti della spedizione');
    } finally {
      setLoadingData(false);
    }
  };

  // Pezzi della riga ancora da mettere in un documento di carico
  const toLoad = (it: ShipmentPackingListItem) => Math.max(0, it.quantity - (it.quantity_in_documents || 0));

  // Il ricevuto può superare lo spedito (eccedenza del fornitore): il backend
  // rettifica la partita (riga d'ordine + riga di spedizione) prima del carico
  const setReceivedQty = (itemId: number, value: number | null) => {
    let v = value ?? 0;
    if (v < 0) v = 0;
    setReceived((prev) => ({ ...prev, [itemId]: v }));
  };

  const totals = useMemo(() => {
    let ricevuto = 0;
    let spedito = 0;
    let eccedenza = 0;
    const surplusLines: string[] = [];
    items.forEach((it) => {
      const r = received[it.id] ?? 0;
      const daCaricare = toLoad(it);
      ricevuto += r;
      spedito += it.quantity;
      if (r > daCaricare) {
        eccedenza += r - daCaricare;
        surplusLines.push(`${it.product_code || `ID ${it.product_id}`}: +${r - daCaricare} pz${it.supplier_order_id ? '' : ' (riga senza ordine fornitore: non ammessa)'}`);
      }
    });
    // residuo = merce ancora da caricare non ricevuta (solo le righe sotto il previsto)
    const residuo = items.reduce((sum, it) => sum + Math.max(0, toLoad(it) - (received[it.id] ?? 0)), 0);
    return { ricevuto, spedito, residuo, eccedenza, surplusLines };
  }, [items, received]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!shipment) return;
    if (totals.ricevuto <= 0) {
      message.error('Inserire almeno una quantità ricevuta');
      return;
    }

    const values = await form.validateFields().catch(() => null);
    if (values === null) return;

    const payload: GenerateLoadDocumentPayload = {
      document_type: values.document_type,
      document_number: values.document_number?.trim() || undefined,
      document_date: values.document_date ? values.document_date.format('YYYY-MM-DD') : undefined,
      notes: values.notes?.trim() || undefined,
      lines: items.map((it) => ({
        shipment_item_id: it.id,
        received_quantity: received[it.id] ?? 0,
        udc_pallet: (udcPallet[it.id] || '').trim() || undefined,
        udc_collo: (udcCollo[it.id] || '').trim() || undefined
      }))
    };
    if (totals.eccedenza > 0) {
      payload.rectification_reason_code = values.rectification_reason_code || 'SUPPLIER_OVERSHIP';
      payload.rectification_notes = values.rectification_notes?.trim() || undefined;
    }

    setLoading(true);
    try {
      const response = await unifiedShipmentsAPI.generateLoadDocument(shipment.id, payload);
      message.success(response?.message || 'Documento di carico creato in bozza', 8);
      onSuccess();
    } catch (error: any) {
      console.error('Errore creazione documento di carico:', error);
      message.error(error.response?.data?.detail || 'Errore durante la creazione del documento di carico');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Prodotto',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (_: any, record: ShipmentPackingListItem) => (
        <span>
          <strong>{record.product_code || `ID ${record.product_id}`}</strong>
          {record.product_description ? ` - ${record.product_description}` : ''}
          {record.supplier_order_number ? (
            <><br /><Text type="secondary" style={{ fontSize: 12 }}>Ordine: {record.supplier_order_number}</Text></>
          ) : null}
        </span>
      )
    },
    {
      title: 'Spedito',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      align: 'right' as const
    },
    {
      title: 'Già a documento',
      key: 'in_documents',
      width: 110,
      align: 'right' as const,
      render: (_: any, record: ShipmentPackingListItem) => record.quantity_in_documents || 0
    },
    {
      title: 'Ricevuto',
      key: 'received',
      width: 120,
      render: (_: any, record: ShipmentPackingListItem) => (
        <InputNumber
          min={0}
          value={received[record.id] ?? 0}
          onChange={(val) => setReceivedQty(record.id, val)}
          status={(received[record.id] ?? 0) > toLoad(record) ? 'warning' : undefined}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'UDC Pallet',
      key: 'udc_pallet',
      width: 140,
      render: (_: any, record: ShipmentPackingListItem) => (
        <Input
          value={udcPallet[record.id] ?? ''}
          onChange={(e) => setUdcPallet((prev) => ({ ...prev, [record.id]: e.target.value }))}
          placeholder="UDC pallet"
        />
      )
    },
    {
      title: 'UDC Collo',
      key: 'udc_collo',
      width: 140,
      render: (_: any, record: ShipmentPackingListItem) => (
        <Input
          value={udcCollo[record.id] ?? ''}
          onChange={(e) => setUdcCollo((prev) => ({ ...prev, [record.id]: e.target.value }))}
          placeholder="UDC collo"
        />
      )
    }
  ];

  return (
    <Modal
      title="Documento di carico (NAZIONALE / UE)"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText="Crea documento in bozza"
      cancelText="Annulla"
      confirmLoading={loading}
      width={920}
      style={{ top: 20 }}
      destroyOnClose
    >
      {shipment && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <Text type="secondary">
            Spedizione: <strong>{shipment.shipment_code}</strong>
            {shipment.description ? <> — {shipment.description}</> : null}
          </Text>
        </div>
      )}

      <Spin spinning={loadingData}>
        <Alert
          type="info"
          message="Indica il tipo di documento, i pezzi effettivamente ricevuti e l'UDC di ogni riga. Il documento nasce in BOZZA: la merce entra in magazzino solo con 'Elabora' in Documenti di Carico. Se ricevi meno del previsto, il resto resta in transito e potrà entrare con un altro documento; la spedizione viene evasa quando tutta la merce è entrata. Se ricevi PIÙ del previsto, l'eccedenza viene aggiunta alla partita (ordine fornitore e spedizione)."
          style={{ marginBottom: 16 }}
        />

        <Table
          rowKey="id"
          dataSource={items}
          columns={columns}
          pagination={false}
          size="small"
          locale={{ emptyText: 'Nessun prodotto nella spedizione' }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>Totali</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right"><strong>{totals.spedito}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} align="right"><strong>{totals.ricevuto}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        {totals.residuo > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={`Merce non ricevuta: ${totals.residuo} pz resteranno in transito (potranno entrare con un altro documento).`}
          />
        )}
        {totals.eccedenza > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={`Eccedenza: ${totals.eccedenza} pz ricevuti in più rispetto allo spedito.`}
            description={
              <>
                <div>La partita viene rettificata subito: riga d'ordine fornitore e riga di spedizione salgono della differenza, con motivo in Cronologia.</div>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{totals.surplusLines.map((l, i) => <li key={i}>{l}</li>)}</ul>
              </>
            }
          />
        )}

        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {totals.eccedenza > 0 && (
            <Row gutter={16}>
              <Col span={10}>
                <Form.Item name="rectification_reason_code" label="Motivo dell'eccedenza" rules={[{ required: true, message: 'Indica il motivo' }]}>
                  <Select>
                    {reasons.map((r) => <Option key={r.code} value={r.code}>{r.label}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item name="rectification_notes" label="Note rettifica (opzionali)">
                  <Input className="no-uppercase-modal" placeholder="Es. conteggio DDT vs colli ricevuti" />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="document_type" label="Tipo documento" rules={[{ required: true, message: 'Scegli il tipo di documento' }]}>
                <Select>
                  {DOCUMENT_TYPE_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name="document_number" label="Numero documento" tooltip="Se vuoto il programma assegna un numero automatico">
                <Input placeholder="Es. 123/2026" />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name="document_date" label="Data documento">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Note (opzionali)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ShipmentLoadModal;
